import React from 'react';
import { Image, Upload, FileImage, Info, CheckCircle, Camera } from 'lucide-react';
import { FileUploader } from '../../common/FileUploader';

export default function ImageTab({ values, setField, t }) {
  return (
    <div className="space-y-8">
      {/* Image Upload Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-orange-50 to-yellow-50 px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <div className="p-2 bg-orange-100 rounded-lg mr-3">
              <Image className="w-5 h-5 text-orange-600" />
            </div>
            {t('subscriptionImage')}
          </h3>
          <p className="text-sm text-gray-600 mt-1">{t('uploadHighQualityImage')}</p>
        </div>
        
        <div className="p-8">
          <div className="max-w-lg mx-auto">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
                <Upload className="w-8 h-8 text-orange-600" />
              </div>
              <h4 className="text-lg font-medium text-gray-900 mb-2">{t('uploadSubscriptionImage')}</h4>
              <p className="text-sm text-gray-500">{t('dragDropOrClickToUpload')}</p>
            </div>
            
            <FileUploader
              label={t("subscriptionImageLabel")}
              onUploadComplete={(key) => setField("imageUrl", key)}
              previewEnabled={true}
              initialKey={values.imageUrl}
              accept="image/*"
              help={t("subscriptionImageHelp")}
              className="border-2 border-dashed border-gray-300 rounded-xl hover:border-orange-400 transition-colors"
            />
          </div>
        </div>
      </div>

      {/* Image Guidelines */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg mr-3">
              <Info className="w-5 h-5 text-blue-600" />
            </div>
            {t('imageGuidelines')}
          </h3>
          <p className="text-sm text-gray-600 mt-1">{t('followGuidelinesForBestResults')}</p>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Technical Specifications */}
            <div className="space-y-4">
              <div className="flex items-center mb-4">
                <FileImage className="w-5 h-5 text-blue-600 mr-2" />
                <h4 className="font-semibold text-blue-900">{t('recommendedSpecs')}</h4>
              </div>
              <div className="space-y-3">
                {[
                  { label: t('imageSize'), value: '1200x800px', icon: '📐' },
                  { label: t('fileFormat'), value: 'JPG, PNG', icon: '📄' },
                  { label: t('maxFileSize'), value: '5MB', icon: '💾' },
                  { label: t('aspectRatio'), value: '3:2', icon: '🖼️' }
                ].map((spec, index) => (
                  <div key={index} className="flex items-center p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <span className="text-lg mr-3">{spec.icon}</span>
                    <div className="flex-1">
                      <span className="text-sm font-medium text-blue-900">{spec.label}: </span>
                      <span className="text-sm text-blue-700">{spec.value}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Best Practices */}
            <div className="space-y-4">
              <div className="flex items-center mb-4">
                <Camera className="w-5 h-5 text-green-600 mr-2" />
                <h4 className="font-semibold text-green-900">{t('bestPractices')}</h4>
              </div>
              <div className="space-y-3">
                {[
                  { text: t('highQuality'), icon: CheckCircle },
                  { text: t('goodLighting'), icon: CheckCircle },
                  { text: t('appetizingFood'), icon: CheckCircle },
                  { text: t('brandConsistent'), icon: CheckCircle }
                ].map((practice, index) => (
                  <div key={index} className="flex items-center p-3 bg-green-50 rounded-lg border border-green-100">
                    <practice.icon className="w-5 h-5 text-green-600 mr-3 flex-shrink-0" />
                    <span className="text-sm text-green-800">{practice.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Additional Tips */}
          <div className="mt-8 p-6 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200">
            <h4 className="font-semibold text-purple-900 mb-3 flex items-center">
              <Info className="w-5 h-5 text-purple-600 mr-2" />
              {t('proTips')}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-purple-800">
              <div className="flex items-start">
                <span className="w-2 h-2 bg-purple-500 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                <span>{t('useNaturalLightTip')}</span>
              </div>
              <div className="flex items-start">
                <span className="w-2 h-2 bg-purple-500 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                <span>{t('showVarietyTip')}</span>
              </div>
              <div className="flex items-start">
                <span className="w-2 h-2 bg-purple-500 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                <span>{t('avoidClutterTip')}</span>
              </div>
              <div className="flex items-start">
                <span className="w-2 h-2 bg-purple-500 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                <span>{t('considerMoodTip')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}